import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { addAttractionOffer } from '~/services/AttractionsService';

import { setCacheBuster } from './attractionCacheBuster';
import { attractionQueryKeys } from './attractionQueryKeys';

interface AddAttractionOfferData {
  attractionId: string;
  offerId: string;
}

export function useAddAttractionOffer() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ attractionId, offerId }: AddAttractionOfferData) => {
      return await addAttractionOffer(attractionId, offerId);
    },
    onSuccess: (_data, { attractionId }) => {
      queryClient.resetQueries({ queryKey: attractionQueryKeys.offers(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      setCacheBuster(attractionId, Date.now().toString());
      const resp = _data as unknown as { result?: { alreadyLinked?: boolean } } | undefined;
      if (resp?.result?.alreadyLinked) {
        enqueueSnackbar('Offer was already linked. Updated to MANUAL match type.', { variant: 'info' });
      } else {
        enqueueSnackbar('Offer linked to attraction', { variant: 'success' });
      }
    },
    onError: (error: Error, { offerId }) => {
      const msg = error.message || '';
      if (msg.includes('404') || msg.includes('not found')) {
        enqueueSnackbar(`Offer "${offerId}" was not found. Check the ID is correct and the offer is online.`, {
          variant: 'warning',
        });
      } else if (msg.includes('409') || msg.includes('already')) {
        enqueueSnackbar(`Offer "${offerId}" is already linked to this attraction.`, { variant: 'info' });
      } else {
        enqueueSnackbar(`Could not link offer. Please try again.`, { variant: 'error' });
      }
    },
  });
}
